import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, CheckCircle, XCircle, Package } from 'lucide-react';
import api from '../../services/api';
import Modal from '../../components/shared/Modal';
import Badge from '../../components/shared/Badge';

// Módulos gerais (sem Estoque — tratado em seção dedicada)
const MODULOS = [
  { key: 'dashboard',       label: 'Dashboard',               group: 'CRM' },
  { key: 'leads',           label: 'Leads / CRM',             group: 'CRM' },
  { key: 'empreendimentos', label: 'Empreendimentos',          group: 'CRM' },
  { key: 'imoveis',         label: 'Imóveis',                 group: 'CRM' },
  { key: 'pipeline',        label: 'Pipeline',                group: 'CRM' },
  { key: 'agenda',          label: 'Agenda de Visitas',        group: 'Operacional' },
  { key: 'whatsapp',        label: 'Comunicações / WhatsApp', group: 'Operacional' },
  { key: 'envios_massa',    label: 'Envios em Massa',          group: 'Operacional' },
  { key: 'propostas',       label: 'Vendas / Propostas',       group: 'Vendas' },
  { key: 'portal_clientes', label: 'Portal do Cliente',        group: 'Portais' },
  { key: 'portal_corretor', label: 'Portal do Corretor',       group: 'Portais' },
  { key: 'marketing',       label: 'Marketing',                group: 'Outros' },
  { key: 'portais',         label: 'Portais de Imóveis',       group: 'Outros' },
  { key: 'configuracoes',   label: 'Configurações',            group: 'Outros' },
];

// Sub-permissões granulares do módulo Estoque
const ESTOQUE_SUBS = [
  { key: 'dashboard',     label: 'Dashboard / Resumo' },
  { key: 'itens',         label: 'Ver Ferramentas e Fichas' },
  { key: 'novo',          label: 'Cadastrar e Editar Itens' },
  { key: 'alertas',       label: 'Manutenções e Alertas' },
  { key: 'gerador_qr',    label: 'Gerar Etiquetas QR' },
  { key: 'scanner',       label: 'Scanner QR' },
  { key: 'configuracoes', label: 'Configurações do Estoque' },
];

const PERFIL_LABELS = { admin: 'Administrador', gerente: 'Gerente', corretor: 'Corretor' };
const PERFIL_COLORS = { admin: 'bg-purple-100 text-purple-700', gerente: 'bg-blue-100 text-blue-700', corretor: 'bg-gray-100 text-gray-700' };

const MODULO_GROUPS = [...new Set(MODULOS.map(m => m.group))];

const EMPTY_FORM = { nome: '', email: '', senha: '', perfil: 'corretor', telefone: '', modulos_permitidos: [], ativo: true };

export default function UsuariosList() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [erro, setErro] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(r => r.data.data),
  });

  const mutation = useMutation({
    mutationFn: (payload) =>
      editing
        ? api.put(`/users/${editing.id}`, payload).then(r => r.data)
        : api.post('/users', payload).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeModal();
    },
    onError: (err) => setErro(err.response?.data?.error || 'Erro ao salvar usuário'),
  });

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setErro('');
    setModal(true);
  }

  function openEdit(user) {
    setEditing(user);
    setForm({
      nome: user.nome,
      email: user.email,
      senha: '',
      perfil: user.perfil,
      telefone: user.telefone || '',
      modulos_permitidos: user.modulos_permitidos || [],
      ativo: user.ativo,
    });
    setErro('');
    setModal(true);
  }

  function closeModal() {
    setModal(false);
    setEditing(null);
    setErro('');
  }

  function toggleModulo(key) {
    setForm(f => {
      const list = f.modulos_permitidos.includes(key)
        ? f.modulos_permitidos.filter(m => m !== key)
        : [...f.modulos_permitidos, key];
      return { ...f, modulos_permitidos: list };
    });
  }

  // ── Estoque helpers ──────────────────────────────────────────────
  const estoqueCompleto = form.modulos_permitidos.includes('estoque');
  const estoqueSubs = form.modulos_permitidos.filter(m => m.startsWith('estoque.'));
  const hasAnyEstoque = estoqueCompleto || estoqueSubs.length > 0;

  function setEstoqueAtivado(ativo) {
    if (ativo) {
      setForm(f => ({
        ...f,
        modulos_permitidos: [...f.modulos_permitidos.filter(m => !m.startsWith('estoque')), 'estoque'],
      }));
    } else {
      setForm(f => ({ ...f, modulos_permitidos: f.modulos_permitidos.filter(m => !m.startsWith('estoque')) }));
    }
  }

  function setEstoqueCompleto(checked) {
    if (checked) {
      setForm(f => ({
        ...f,
        modulos_permitidos: [...f.modulos_permitidos.filter(m => !m.startsWith('estoque')), 'estoque'],
      }));
    } else {
      // Mantém as subs já marcadas, remove só o 'estoque' completo
      setForm(f => ({ ...f, modulos_permitidos: f.modulos_permitidos.filter(m => m !== 'estoque') }));
    }
  }

  function toggleEstoqueSub(sub) {
    const key = `estoque.${sub}`;
    if (estoqueCompleto) {
      // Estava em modo completo: vai para parcial com tudo exceto esta sub
      const allSubs = ESTOQUE_SUBS.map(s => `estoque.${s.key}`).filter(k => k !== key);
      setForm(f => ({
        ...f,
        modulos_permitidos: [...f.modulos_permitidos.filter(m => !m.startsWith('estoque')), ...allSubs],
      }));
    } else {
      setForm(f => {
        const list = f.modulos_permitidos.includes(key)
          ? f.modulos_permitidos.filter(m => m !== key)
          : [...f.modulos_permitidos, key];
        return { ...f, modulos_permitidos: list };
      });
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    setErro('');
    const payload = {
      nome: form.nome,
      email: form.email,
      perfil: form.perfil,
      telefone: form.telefone || null,
      ativo: form.ativo,
      modulos_permitidos: form.perfil === 'admin' ? null : form.modulos_permitidos,
    };
    if (!editing || form.senha) payload.senha = form.senha;
    mutation.mutate(payload);
  }

  const isAdmin = form.perfil === 'admin';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Usuários</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary">
          <Plus size={16} /> Novo Usuário
        </button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Carregando...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left">Nome</th>
                <th className="px-6 py-3 text-left">E-mail</th>
                <th className="px-6 py-3 text-left">Perfil</th>
                <th className="px-6 py-3 text-left">Módulos</th>
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data || []).map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">{user.nome}</td>
                  <td className="px-6 py-4 text-gray-500">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${PERFIL_COLORS[user.perfil]}`}>
                      {PERFIL_LABELS[user.perfil]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500 max-w-xs">
                    {user.perfil === 'admin' || !user.modulos_permitidos
                      ? <span className="text-xs text-purple-600 font-medium">Acesso total</span>
                      : user.modulos_permitidos.length === 0
                        ? <span className="text-xs text-red-500">Sem acesso</span>
                        : (() => {
                            const mods = user.modulos_permitidos;
                            const estoqueOk = mods.includes('estoque');
                            const estoqueSubsN = mods.filter(m => m.startsWith('estoque.')).length;
                            const outros = mods.filter(m => !m.startsWith('estoque'));
                            return (
                              <span className="flex flex-wrap gap-1">
                                {outros.map(m => {
                                  const mod = MODULOS.find(x => x.key === m);
                                  return (
                                    <span key={m} className="inline-flex px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                                      {mod?.label || m}
                                    </span>
                                  );
                                })}
                                {estoqueOk && <span className="inline-flex px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-xs">Estoque completo</span>}
                                {!estoqueOk && estoqueSubsN > 0 && (
                                  <span className="inline-flex px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded text-xs">
                                    Estoque ({estoqueSubsN}/{ESTOQUE_SUBS.length})
                                  </span>
                                )}
                              </span>
                            );
                          })()
                    }
                  </td>
                  <td className="px-6 py-4">
                    {user.ativo
                      ? <span className="flex items-center gap-1 text-xs text-green-600"><CheckCircle size={14} /> Ativo</span>
                      : <span className="flex items-center gap-1 text-xs text-red-500"><XCircle size={14} /> Inativo</span>
                    }
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => openEdit(user)} className="text-gray-400 hover:text-primary p-1 rounded">
                      <Pencil size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal isOpen={modal} onClose={closeModal} title={editing ? 'Editar Usuário' : 'Novo Usuário'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                value={form.nome}
                onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Senha {editing ? '(deixe em branco para não alterar)' : '*'}
              </label>
              <input
                type="password"
                value={form.senha}
                onChange={e => setForm(f => ({ ...f, senha: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                placeholder="••••••"
                required={!editing}
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <input
                value={form.telefone}
                onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                placeholder="(44) 99999-9999"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Perfil *</label>
              <select
                value={form.perfil}
                onChange={e => setForm(f => ({ ...f, perfil: e.target.value, modulos_permitidos: [] }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
              >
                <option value="corretor">Corretor</option>
                <option value="gerente">Gerente</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            {editing && (
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-primary"
                  />
                  <span className="text-sm font-medium text-gray-700">Usuário ativo</span>
                </label>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                Módulos permitidos
                {isAdmin && <span className="ml-2 text-xs text-purple-600 font-normal">(Admin tem acesso total)</span>}
              </label>
              {!isAdmin && (
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm(f => ({ ...f, modulos_permitidos: [...MODULOS.map(m => m.key), 'estoque'] }))}
                    className="text-xs text-blue-600 hover:underline">Marcar todos</button>
                  <span className="text-gray-300">|</span>
                  <button type="button" onClick={() => setForm(f => ({ ...f, modulos_permitidos: [] }))}
                    className="text-xs text-gray-500 hover:underline">Limpar</button>
                  <span className="text-gray-300">|</span>
                  <button type="button" onClick={() => setForm(f => ({ ...f, modulos_permitidos: ['portal_corretor'] }))}
                    className="text-xs text-orange-600 hover:underline">Só Portal Corretor</button>
                  <span className="text-gray-300">|</span>
                  <button type="button" onClick={() => setForm(f => ({ ...f, modulos_permitidos: ['estoque'] }))}
                    className="text-xs text-green-600 hover:underline">Só Estoque</button>
                </div>
              )}
            </div>
            <div className="space-y-3 border border-gray-100 rounded-xl p-3 bg-gray-50">
              {MODULO_GROUPS.map(group => (
                <div key={group}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{group}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {MODULOS.filter(m => m.group === group).map(({ key, label }) => (
                      <label
                        key={key}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                          isAdmin
                            ? 'bg-purple-50 border-purple-200 text-purple-500 cursor-not-allowed'
                            : form.modulos_permitidos.includes(key)
                              ? 'bg-blue-50 border-blue-400 text-gray-800'
                              : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isAdmin || form.modulos_permitidos.includes(key)}
                          onChange={() => !isAdmin && toggleModulo(key)}
                          disabled={isAdmin}
                          className="w-3.5 h-3.5 rounded"
                        />
                        <span className="text-xs">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Seção Estoque — permissões granulares */}
          {!isAdmin && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              {/* Header */}
              <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 transition-colors ${hasAnyEstoque ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  <Package size={16} className={hasAnyEstoque ? 'text-blue-600' : 'text-gray-400'} />
                  <span className={`text-sm font-semibold ${hasAnyEstoque ? 'text-gray-800' : 'text-gray-500'}`}>
                    Módulo Estoque
                  </span>
                  {estoqueCompleto && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Acesso completo</span>}
                  {!estoqueCompleto && estoqueSubs.length > 0 && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                      {estoqueSubs.length}/{ESTOQUE_SUBS.length} permissões
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {hasAnyEstoque && (
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={estoqueCompleto}
                        onChange={e => setEstoqueCompleto(e.target.checked)}
                        className="w-3.5 h-3.5 rounded"
                      />
                      <span className="text-xs text-gray-600">Tudo</span>
                    </label>
                  )}
                  {/* Toggle ativar/desativar */}
                  <button
                    type="button"
                    onClick={() => setEstoqueAtivado(!hasAnyEstoque)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${hasAnyEstoque ? 'bg-blue-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hasAnyEstoque ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              {/* Sub-permissões */}
              {hasAnyEstoque && (
                <div className="p-3 grid grid-cols-2 gap-1.5 bg-white">
                  {ESTOQUE_SUBS.map(({ key, label }) => {
                    const isChecked = estoqueCompleto || form.modulos_permitidos.includes(`estoque.${key}`);
                    return (
                      <label
                        key={key}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs transition-colors ${
                          isChecked
                            ? 'bg-blue-50 border-blue-300 text-gray-800'
                            : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleEstoqueSub(key)}
                          className="w-3.5 h-3.5 rounded flex-shrink-0"
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              )}

              {!hasAnyEstoque && (
                <div className="px-4 py-3 text-xs text-gray-400 bg-white">
                  Clique no toggle para liberar acesso ao módulo Estoque com permissões personalizadas.
                </div>
              )}
            </div>
          )}

          {erro && <p className="text-red-500 text-sm">{erro}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary disabled:opacity-50"
            >
              {mutation.isPending ? 'Salvando...' : editing ? 'Salvar alterações' : 'Criar usuário'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
